/*!
 * Copyright 2019 Fondazione Bruno Kessler
 * Licensed under the EUPL, Version 1.1 or - as soon they will be approved by the European
 * Commission - subsequent versions of the EUPL (the "Licence"); You may not use this work
 * except in compliance with the Licence.
 *
 * You may obtain a copy of the Licence at:
 * http://ec.europa.eu/idabc/eupl.html
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the Licence is distributed on an "AS IS" basis, WITHOUT WARRANTIES OR CONDITIONS OF
 * ANY KIND, either express or implied. See the Licence for the specific language
 * governing permissions and limitations under the Licence.
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from "@angular/core";
import {TrecApiError} from "@trec/api/errors";
import {PhonecodeAuthenticatorService} from "@trec/api/phonecode-authenticator";
import {ActivationCodeType} from "@trec/api/salute-helper";
import {C3SnackbarStatusService} from "@trec/components/snackbar-status";
import {differenceInSeconds, formatDistanceToNowStrict} from "date-fns";
import {it} from "date-fns/locale";
import {finalize} from "rxjs/operators";

@Component({
  selector: "c3-app-activation-code-view",
  templateUrl: "./app-activation-code-view.component.html",
  styleUrls: ["./app-activation-code-view.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AppActivationCodeViewComponent implements OnDestroy, OnInit {
  @Input()
  public set patientFiscalCode(patientFc: string) {
  }

  loading = false;

  @Output() activationCodeEmitter = new EventEmitter<string>();
  activationCode: string | null = null;
  activationCodeExpirationDate: Date | null = null;

  get isExpired(): boolean | null {
    if (this.activationCodeExpirationDate === null) {
      return null;
    }

    return this.activationCodeExpirationDate < new Date();
  }

  countStartTime: Date | null = null;
  remainingTime = "";
  remainingPercentage = 0;
  activationCodeConfig: ActivationCodeType | null;

  private timerId: any | null = null;

  constructor(
    private cd: ChangeDetectorRef,
    private phonecodeAuth: PhonecodeAuthenticatorService,
    private c3Snackbar: C3SnackbarStatusService
  ) {
    let configuratoreActivationCode = localStorage.getItem("configuratoreActivationCode");
    if (configuratoreActivationCode) {
      this.activationCodeConfig = JSON.parse(configuratoreActivationCode);
    }
  }
  ngOnInit(): void {
  }

  updateAppActivationCode(fiscalCode: string, email?: string, telefono?: string): void {
    this.loading = true;

    this.phonecodeAuth
      .generateActivationCodeV3(fiscalCode, email, telefono)
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cd.markForCheck();
        })
      )
      .subscribe(
        (codeData) => {
          this.activationCode = codeData.code;
          this.countStartTime = new Date();
          this.activationCodeExpirationDate = new Date(codeData.expiration_date);
          this.startTimer();
          this.c3Snackbar.showSuccess({message: codeData.message});
          this.activationCodeEmitter.emit(this.activationCode);
        },
        (error: TrecApiError) => {
          this.c3Snackbar.showError({
            error,
            messagesMap: {},
            hideDetails: false,
            hideKibanaBtn: true,
          }),
            this.clearTimer();
        }
      );
  }

  private startTimer() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }

    this.updateDisplayedRemainingTime();

    setInterval(() => {
      this.updateDisplayedRemainingTime();
    }, 5000);
  }

  private clearTimer() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
    this.countStartTime = null;
    this.remainingTime = "";
    this.remainingPercentage = 0;
  }

  private updateDisplayedRemainingTime() {
    if (this.activationCodeExpirationDate) {
      this.remainingTime = formatDistanceToNowStrict(this.activationCodeExpirationDate, {
        locale: it,
        addSuffix: true,
      });

      if (this.countStartTime) {
        const totalSeconds = differenceInSeconds(
          this.activationCodeExpirationDate,
          this.countStartTime
        );
        const missingSeconds = differenceInSeconds(this.activationCodeExpirationDate, new Date());
        this.remainingPercentage = missingSeconds / totalSeconds;
      }

      this.cd.markForCheck();
    }
  }

  ngOnDestroy() {
    this.clearTimer();
  }
}
